import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Plus, Trash2, Camera, X, ChevronRight, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import AddCustomerModal from '../components/AddCustomerModal';
import CustomerSelectionModal from '../components/CustomerSelectionModal';
import EditCustomerModal from '../components/EditCustomerModal';
import ImageEditorModal from '../components/ImageEditorModal';
import { RatesSection } from '../components/RatesSection';
import { PaymentTypeSelector, CashPaymentModal, CardPaymentModal, UPIPaymentModal } from '../components/PaymentModals';
import { BankTransferModal, OldGoldModal } from '../components/PaymentModalsExtended';
import { MetalCreditModal } from '../components/MetalCreditModalNew';
import { PaymentSummary } from '../components/PaymentSummary';
import { uploadToCloudinary, isBase64DataUrl, isCloudinaryUrl } from '../services/cloudinaryService';

const CreateSelectionStepper = ({ user, editMode = false, existingSelection = null }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(existingSelection?.customer_id || '');
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectionDate, setSelectionDate] = useState(
    existingSelection?.selection_date || new Date().toISOString().split('T')[0]
  );
  
  // Check if selection date is backdated
  const isBackdate = selectionDate < new Date().toISOString().split('T')[0];
  const [photos, setPhotos] = useState(existingSelection?.photos || []);
  const [itemsDescription, setItemsDescription] = useState(existingSelection?.items_description || '');
  const [customerRemarks, setCustomerRemarks] = useState(existingSelection?.customer_remarks || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Image editing
  const [editingImage, setEditingImage] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Rate fields (reusing exact same as CreateOrderStepper)
  const [rateMode, setRateMode] = useState('single');
  const [ratePolicy, setRatePolicy] = useState(existingSelection?.rate_policy || 'none');
  const [base24kRate, setBase24kRate] = useState(existingSelection?.base24k_rate_ex_gst?.toString() || '');
  const [gstInputStyle, setGstInputStyle] = useState(existingSelection?.gst_input_style || 'EXCL');
  const [lockDate, setLockDate] = useState('');
  const [purityRates, setPurityRates] = useState({});
  const [purityRateLinks, setPurityRateLinks] = useState({});
  
  // Master Data from Settings
  const [purityOptions, setPurityOptions] = useState([]);
  const fallbackPurities = ['24K', '22K', '18K'];
  
  const [purityMultipliers, setPurityMultipliers] = useState({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
  const [gstPercent, setGstPercent] = useState(3.0);
  const [taxScope, setTaxScope] = useState('TAXABLE');
  const [gstApplicable, setGstApplicable] = useState(true);
  
  // Payment fields
  const [advancePayments, setAdvancePayments] = useState(existingSelection?.advance_payments || []);
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false);
  const [activePaymentModal, setActivePaymentModal] = useState(null);

  useEffect(() => {
    loadCustomers();
    loadGSTConfig();
  }, []);

  // Load customer data in edit mode
  useEffect(() => {
    if (editMode && existingSelection && customers.length > 0) {
      const customer = customers.find(c => c.id === existingSelection.customer_id);
      if (customer) {
        setSelectedCustomerData(customer);
      }
    }
  }, [editMode, existingSelection, customers]);

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
      toast.error('Failed to load customers');
    }
  };

  const loadGSTConfig = async () => {
    try {
      const [gstRes, purityRes] = await Promise.all([
        axiosInstance.get('/masters/gst-config'),
        axiosInstance.get('/masters/purity-options')
      ]);
      
      if (gstRes.data) {
        setGstPercent(gstRes.data.gst_percent || 3.0);
      }
      
      const activePurities = (purityRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      if (activePurities.length > 0) {
        setPurityOptions(activePurities.map(p => p.name));
        const multipliers = {};
        activePurities.forEach(p => {
          multipliers[p.name] = p.multiplier;
        });
        setPurityMultipliers(multipliers);
      } else {
        setPurityOptions(fallbackPurities);
        setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
      }
    } catch (err) {
      console.error('Error loading GST config:', err);
      setPurityOptions(fallbackPurities);
      setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
    }
  };

  const handleCustomerAdded = (newCustomer) => {
    console.log('[CUSTOMER_ADDED] New customer:', newCustomer);
    
    // Add to customers list
    setCustomers(prev => [...prev, newCustomer]);
    
    // IMMEDIATELY set both ID and data
    setSelectedCustomer(newCustomer.id);
    setSelectedCustomerData(newCustomer);
    
    // Close modal
    setShowAddCustomerModal(false);
    
    console.log('[CUSTOMER_ADDED] Set selected customer data:', newCustomer.name);
    toast.success('Customer added and selected successfully');
  };
  
  // Remove useMemo - we now store customer data directly

  const handlePhotoCapture = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Process one file at a time
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (event) => {
      setEditingImage(event.target.result);
      setEditingImageIndex(null); // null means new photo
    };
    
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    
    reader.readAsDataURL(file);
    
    // Reset input to allow selecting same file again
    e.target.value = '';
  };

  const handleImageEdited = async (editedImageData) => {
    try {
      let imageUrl;
      
      // editedImageData can be: {edited: base64, original: base64} or just a URL string
      const imageToUpload = editedImageData.edited || editedImageData.original || editedImageData;
      
      // Upload to Cloudinary if it's base64
      if (isBase64DataUrl(imageToUpload)) {
        toast.info('Uploading photo...');
        imageUrl = await uploadToCloudinary(imageToUpload);
      } else if (isCloudinaryUrl(imageToUpload)) {
        imageUrl = imageToUpload;
      } else {
        // If it's already a URL string
        imageUrl = imageToUpload;
      }

      if (editingImageIndex !== null) {
        // Replace existing photo
        const updatedPhotos = [...photos];
        updatedPhotos[editingImageIndex] = imageUrl;
        setPhotos(updatedPhotos);
        toast.success('Photo updated');
      } else {
        // Add new photo
        setPhotos([...photos, imageUrl]);
        toast.success('Photo added');
      }

      setEditingImage(null);
      setEditingImageIndex(null);
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast.error('Failed to upload photo');
      setEditingImage(null);
      setEditingImageIndex(null);
    }
  };

  const handleEditPhoto = (index) => {
    setEditingImage(photos[index]);
    setEditingImageIndex(index);
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
    toast.success('Photo removed');
  };

  const handleAddPayment = (paymentData) => {
    setAdvancePayments([...advancePayments, paymentData]);
    setActivePaymentModal(null);
    setShowPaymentTypeSelector(false);
    toast.success('Payment added');
  };

  const handleRemovePayment = (index) => {
    setAdvancePayments(advancePayments.filter((_, i) => i !== index));
    toast.success('Payment removed');
  };

  const handleBase24kChange = (value) => {
    setBase24kRate(value);
    if (value && !isNaN(value)) {
      computePurityRates(value);
    } else {
      setPurityRates({});
      setPurityRateLinks({});
    }
  };

  const computePurityRates = (base24k) => {
    const computed = {};
    const links = {};
    Object.keys(purityMultipliers).forEach(purity => {
      if (purity !== '24K') {
        computed[purity] = Math.round(parseFloat(base24k) * purityMultipliers[purity]);
        links[purity] = true;
      }
    });
    setPurityRates(computed);
    setPurityRateLinks(links);
  };

  const handlePurityMultiplierChange = (purity, value) => {
    setPurityRates(prev => ({
      ...prev,
      [purity]: value
    }));
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: false
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (!selectionDate) {
      toast.error('Please select a date');
      return;
    }
    if (photos.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }

    if (ratePolicy === 'lock-today' && !base24kRate) {
      toast.error('Please enter 24K gold rate');
      return;
    }

    setIsSubmitting(true);
    try {
      // Extract photo URLs from objects (ImageEditorModal returns objects with url and public_id)
      const photoUrls = photos.map(photo => {
        if (typeof photo === 'string') {
          return photo;
        } else if (photo && photo.url) {
          return photo.url;
        } else if (photo && photo.edited) {
          return photo.edited;
        }
        return photo;
      }).filter(Boolean);

      const selectionData = {
        customer_id: selectedCustomer,
        selection_date: selectionDate,
        photos: photoUrls,
        items_description: itemsDescription && itemsDescription.trim() ? itemsDescription.trim() : null,
        customer_remarks: customerRemarks && customerRemarks.trim() ? customerRemarks.trim() : null,
        rate_policy: ratePolicy || 'none',
        base24k_rate_ex_gst: (ratePolicy === 'lock-today' && base24kRate) ? parseFloat(base24kRate) : null,
        gst_input_style: gstInputStyle || 'EXCL',
        advance_payments: advancePayments || []
      };

      console.log('Submitting selection data:', JSON.stringify(selectionData, null, 2));

      let response;
      if (editMode && existingSelection) {
        // Update existing selection
        response = await axiosInstance.put(`/selections/${existingSelection.selection_id}`, selectionData);
        toast.success('Selection updated successfully!');
      } else {
        // Create new selection
        response = await axiosInstance.post('/selections', selectionData);
        toast.success('Selection created successfully!');
      }
      
      console.log('Selection response - Full response:', response.data);
      
      // Handle multiple possible ID field names
      const selectionId = response.data.selection_id || response.data.id || response.data._id || existingSelection?.selection_id;
      
      console.log('Extracted selection ID:', selectionId);
      
      if (!selectionId) {
        console.error('No selection ID found in response:', response.data);
        throw new Error('Selection ID not returned from server');
      }
      
      // Navigate to selection detail page
      setTimeout(() => {
        const navPath = `/selections/${selectionId}`;
        console.log('Navigating to:', navPath);
        navigate(navPath);
      }, 1500);
    } catch (err) {
      console.error('Error saving selection:', err);
      console.error('Error response:', err.response?.data);
      
      // Handle Pydantic validation errors
      let errorMessage = 'Failed to save selection';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // Check if it's a Pydantic validation error (array of objects)
        if (Array.isArray(detail)) {
          // Extract field names and messages from validation errors
          const errors = detail.map(e => `${e.loc?.[e.loc.length - 1] || 'field'}: ${e.msg}`).join(', ');
          errorMessage = `Validation error: ${errors}`;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(editMode ? `/selections/${existingSelection.selection_id}` : '/selections')}
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
              disabled={isSubmitting}
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-800">
                {editMode ? 'Edit Customer Selection' : 'New Customer Selection'}
              </h1>
              <p className="text-sm text-gray-500">
                {editMode ? `Editing ${existingSelection?.selection_id}` : "Record customer's item preferences"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        {/* Step 1: Customer Selection */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="text-xl">1️⃣</span>
              <span>Customer Details</span>
            </h2>
          </div>
          
          {selectedCustomer && selectedCustomerData ? (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-800">{selectedCustomerData.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">📱 {selectedCustomerData.mobile}</p>
                  {selectedCustomerData.alternate_mobile && (
                    <p className="text-sm text-gray-600">📞 {selectedCustomerData.alternate_mobile}</p>
                  )}
                  {selectedCustomerData.email && (
                    <p className="text-xs text-gray-500 mt-1">✉️ {selectedCustomerData.email}</p>
                  )}
                  {selectedCustomerData.address && (
                    <p className="text-xs text-gray-500 mt-1">📍 {selectedCustomerData.address}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCustomer(selectedCustomerData);
                      setShowEditCustomerModal(true);
                    }}
                    className="px-3 py-2 bg-white border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 font-semibold text-sm flex items-center gap-1"
                  >
                    <Edit3 className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCustomer('');
                      setSelectedCustomerData(null);
                      console.log('[CUSTOMER] Customer deselected');
                    }}
                    className="px-4 py-2 bg-white border-2 border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 font-semibold text-sm"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCustomerModal(true)}
              className="w-full px-6 py-4 border-2 border-dashed border-amber-300 rounded-xl hover:border-amber-500 hover:bg-amber-50 transition-all text-amber-700 font-bold text-base flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Select Customer
            </button>
          )}

          {/* Selection Date */}
          <div className="mt-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">Selection Date *</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={selectionDate}
                onChange={(e) => setSelectionDate(e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-amber-200 rounded-lg focus:outline-none focus:border-amber-500 font-semibold"
              />
              {isBackdate && (
                <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-semibold rounded whitespace-nowrap">
                  Back-dated
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Photos */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="text-xl">2️⃣</span>
              <span>Item Photos ({photos.length})</span>
            </h2>
          </div>
          
          {photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
              {photos.map((photo, index) => {
                // Extract URL from photo (can be string or object)
                const photoUrl = typeof photo === 'string' ? photo : (photo?.url || photo?.edited || photo);
                return (
                  <div key={index} className="relative group">
                    <img
                      src={photoUrl}
                      alt={`Selection ${index + 1}`}
                      className="w-full h-32 sm:h-40 object-cover rounded-lg border-2 border-amber-200 cursor-pointer hover:border-amber-400 transition-all"
                      onClick={() => setPreviewImage(photoUrl)}
                      onError={(e) => {
                        // If image fails to load, show placeholder
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Always visible buttons on mobile, hover on desktop */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPhoto(index);
                        }}
                        className="p-1.5 sm:p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 shadow-lg touch-target"
                        title="Edit photo"
                      >
                        <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removePhoto(index);
                        }}
                        className="p-1.5 sm:p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg touch-target"
                        title="Remove photo"
                      >
                        <X className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <label 
            htmlFor="photo-input"
            className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-amber-300 rounded-xl cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all">
            <Camera className="w-10 h-10 text-amber-600 mb-2" />
            <span className="text-base font-bold text-amber-700">Upload Photos</span>
            <span className="text-xs text-gray-500 mt-1">Click to select images (No limit)</span>
            <input
              id="photo-input"
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoCapture}
              className="hidden"
            />
          </label>
        </div>

        {/* Step 3: Description & Remarks */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="text-xl">3️⃣</span>
              <span>Details</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Items Description
              </label>
              <textarea
                value={itemsDescription}
                onChange={(e) => setItemsDescription(e.target.value)}
                placeholder={editMode ? "" : "E.g., 22K gold necklace set, approx 50g, kundan work..."}
                rows={4}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Customer Remarks (Internal Notes)
              </label>
              <textarea
                value={customerRemarks}
                onChange={(e) => setCustomerRemarks(e.target.value)}
                placeholder={editMode ? "" : "What customer mentioned, sizing notes, preferences..."}
                rows={3}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
        </div>

        {/* Step 4: Rate Policy (Optional) - Using exact same component as Orders */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="text-xl">4️⃣</span>
              <span>Rate Lock (Optional)</span>
            </h2>
          </div>
          
          <RatesSection
            taxScope={taxScope}
            setTaxScope={setTaxScope}
            gstApplicable={gstApplicable}
            setGstApplicable={setGstApplicable}
            gstInputStyle={gstInputStyle}
            setGstInputStyle={setGstInputStyle}
            gstPercent={gstPercent}
            rateMode={rateMode}
            setRateMode={setRateMode}
            base24kRate={base24kRate}
            handleBase24kChange={handleBase24kChange}
            purityRates={purityRates}
            handlePurityRateChange={handlePurityMultiplierChange}
            purityRateLinks={purityRateLinks}
            togglePurityLink={() => {}}
            items={[]}
            itemRates={{}}
            setItemRates={() => {}}
            ratePolicy={ratePolicy}
            setRatePolicy={setRatePolicy}
            lockDate={lockDate}
            setLockDate={setLockDate}
          />
        </div>

        {/* Step 5: Advance Payment (Optional) - Using exact same component as Orders */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="text-xl">5️⃣</span>
              <span>Advance Payment (Optional)</span>
            </h2>
          </div>
          
          {advancePayments.length > 0 && (
            <PaymentSummary
              payments={advancePayments}
              onRemove={handleRemovePayment}
            />
          )}

          {!showPaymentTypeSelector && (
            <button
              onClick={() => setShowPaymentTypeSelector(true)}
              className="w-full mt-4 px-6 py-3 border-2 border-dashed border-amber-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all text-amber-700 font-bold flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Advance Payment
            </button>
          )}
        </div>

        {/* Submit Button */}
        <div className="bg-white border-t-2 border-amber-200 rounded-xl p-4 sticky bottom-0 shadow-lg">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedCustomer || photos.length === 0}
            className="w-full px-6 py-4 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                {editMode ? 'Updating Selection...' : 'Creating Selection...'}
              </>
            ) : (
              <>
                {editMode ? 'Update Selection' : 'Create Selection'}
                <ChevronRight className="w-6 h-6" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <CustomerSelectionModal
          customers={customers}
          onCustomerSelected={(customer) => {
            setSelectedCustomer(customer.id);
            setSelectedCustomerData(customer);
            setShowCustomerModal(false);
            console.log('[CUSTOMER_SELECTED] From modal:', customer.name);
          }}
          onClose={() => setShowCustomerModal(false)}
          onAddNew={() => {
            setShowCustomerModal(false);
            setShowAddCustomerModal(true);
          }}
        />
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <AddCustomerModal
          onClose={() => setShowAddCustomerModal(false)}
          onCustomerAdded={handleCustomerAdded}
        />
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
          onCustomerUpdated={(updatedCustomer) => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
            setSelectedCustomerData(updatedCustomer);
            loadCustomers();
          }}
        />
      )}

      {/* Image Editor Modal */}
      {editingImage && (
        <ImageEditorModal
          imageData={editingImage}
          onSave={handleImageEdited}
          onClose={() => {
            setEditingImage(null);
            setEditingImageIndex(null);
          }}
          returnBothImages={true}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100"
          >
            <X className="w-6 h-6 text-gray-800" />
          </button>
          <div className="max-w-4xl max-h-screen">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-screen object-contain"
            />
          </div>
        </div>
      )}

      {/* Payment Type Selector Modal */}
      <PaymentTypeSelector
        isOpen={showPaymentTypeSelector}
        onClose={() => setShowPaymentTypeSelector(false)}
        onSelectType={(type) => {
          setShowPaymentTypeSelector(false);
          setActivePaymentModal(type);
        }}
      />

      {/* Payment Modals - All existing ones */}
      {activePaymentModal === 'cash' && (
        <CashPaymentModal
          isOpen={true}
          onSave={handleAddPayment}
          onClose={() => setActivePaymentModal(null)}
        />
      )}
      {activePaymentModal === 'card' && (
        <CardPaymentModal
          isOpen={true}
          onSave={handleAddPayment}
          onClose={() => setActivePaymentModal(null)}
        />
      )}
      {activePaymentModal === 'upi' && (
        <UPIPaymentModal
          isOpen={true}
          onSave={handleAddPayment}
          onClose={() => setActivePaymentModal(null)}
        />
      )}
      {activePaymentModal === 'bank' && (
        <BankTransferModal
          isOpen={true}
          mode="IMPS"
          onSave={handleAddPayment}
          onClose={() => setActivePaymentModal(null)}
        />
      )}
      {activePaymentModal === 'old-gold' && (
        <OldGoldModal
          isOpen={true}
          onSave={handleAddPayment}
          onClose={() => setActivePaymentModal(null)}
        />
      )}
      {activePaymentModal === 'metal' && (
        <MetalCreditModal
          isOpen={true}
          onSave={handleAddPayment}
          onClose={() => setActivePaymentModal(null)}
        />
      )}
    </div>
  );
};

export default CreateSelectionStepper;
